import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { BellIcon, CloudDownloadIcon, LockIcon, PlugIcon, UserCogIcon } from '@air/design-icons';

import { type SettingsNavItem } from '~/components/v2/components/workspace/settings-nav/settingsNavTypes';
import { SETTINGS_TITLES } from '~/components/v2/constants/settingsTitles';
import { Routes } from '~/constants/routes';
import { useAccountIsFederated } from '~/swr-hooks/account/useAccountIsFederated';
import { insertIf } from '~/utils/insertIf';
import { isPathnameOrNested } from '~/utils/isPathnameOrNested';

export const useAccountSettingsNavItems = () => {
  const { pathname } = useRouter();
  const { accountIsFederated } = useAccountIsFederated();
  const shouldShowChangePassword = !accountIsFederated;

  const items = useMemo<SettingsNavItem[]>(
    () => [
      {
        id: 'account',
        name: SETTINGS_TITLES.ACCOUNT_SETTINGS,
        isActive: isPathnameOrNested(pathname, Routes.account.myAccount),
        href: Routes.account.myAccount,
        icon: UserCogIcon,
      },
      ...insertIf(shouldShowChangePassword, () => [
        {
          id: 'change-password',
          name: SETTINGS_TITLES.CHANGE_PASSWORD,
          isActive: isPathnameOrNested(pathname, Routes.account.changePassword),
          href: Routes.account.changePassword,
          icon: LockIcon,
        },
      ]),
      {
        id: 'notifications',
        name: SETTINGS_TITLES.NOTIFICATIONS,
        isActive: isPathnameOrNested(pathname, Routes.account.notificationSettings),
        href: Routes.account.notificationSettings,
        icon: BellIcon,
      },
      {
        id: 'api-access',
        name: SETTINGS_TITLES.API_ACCESS,
        isActive: isPathnameOrNested(pathname, Routes.account.apiAccess),
        href: Routes.account.apiAccess,
        icon: CloudDownloadIcon,
      },
      {
        id: 'integrations',
        name: SETTINGS_TITLES.INTEGRATIONS,
        isActive: isPathnameOrNested(pathname, Routes.account.integrations),
        href: Routes.account.integrations,
        icon: PlugIcon,
      },
    ],
    [pathname, shouldShowChangePassword],
  );

  return { items };
};
